import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { format } from 'date-fns';
import { 
  Users, 
  FileText, 
  DollarSign, 
  TrendingUp,
  Send,
  Eye,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Plus,
  ExternalLink,
  Copy,
  Mail,
  Calendar as CalendarIcon,
  ChevronRight,
  MoreVertical,
  Trash2,
  Edit,
  Bell,
  Target,
  Loader2,
  AlertTriangle,
  Search,
  Phone,
  Building2,
  Bot,
  UserPlus,
  Globe,
  BarChart3
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPOSAL_STATUS_CONFIG = {
  draft: { color: 'bg-gray-100 text-gray-700', label: 'Draft', icon: <FileText className="h-4 w-4" /> },
  sent: { color: 'bg-blue-100 text-blue-700', label: 'Sent', icon: <Send className="h-4 w-4" /> },
  viewed: { color: 'bg-amber-100 text-amber-700', label: 'Viewed', icon: <Eye className="h-4 w-4" /> },
  pending: { color: 'bg-purple-100 text-purple-700', label: 'Pending', icon: <Clock className="h-4 w-4" /> },
  accepted: { color: 'bg-emerald-100 text-emerald-700', label: 'Accepted', icon: <CheckCircle2 className="h-4 w-4" /> },
  declined: { color: 'bg-red-100 text-red-700', label: 'Declined', icon: <XCircle className="h-4 w-4" /> },
  expired: { color: 'bg-gray-100 text-gray-500', label: 'Expired', icon: <Clock className="h-4 w-4" /> }
};

const LEAD_STATUS_CONFIG = {
  new: { color: 'bg-blue-500', label: 'New' },
  contacted: { color: 'bg-indigo-500', label: 'Contacted' },
  proposal_sent: { color: 'bg-purple-500', label: 'Proposal Sent' },
  proposal_viewed: { color: 'bg-amber-500', label: 'Proposal Viewed' },
  negotiating: { color: 'bg-orange-500', label: 'Negotiating' },
  won: { color: 'bg-emerald-500', label: 'Won' },
  lost: { color: 'bg-red-500', label: 'Lost' }
};

export default function PipelineDashboard() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [docTypeFilter, setDocTypeFilter] = useState('all'); // all, proposal, invoice
  
  // Task dialog
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [analyticsMap, setAnalyticsMap] = useState({});
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    due_date: null,
    priority: 'medium',
    related_to: '',
    related_type: 'proposal'
  });

  // Leads state
  const [leads, setLeads] = useState([]);
  const [leadFilter, setLeadFilter] = useState('all');
  const [leadSearch, setLeadSearch] = useState('');
  const [leadPage, setLeadPage] = useState(0);
  const LEADS_PER_PAGE = 25;

  // Bulk selection
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Delete confirmation dialog
  const [deleteTarget, setDeleteTarget] = useState(null); // {type:'single'|'bulk', leadId?:string}

  // Email queue state
  const [emailQueue, setEmailQueue] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [emailEditForm, setEmailEditForm] = useState({ subject: '', body: '' });

  // Proposal Analytics modal
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsProposal, setAnalyticsProposal] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [overviewRes, proposalsRes, tasksRes, analyticsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/pipeline/overview`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/pipeline/proposals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/follow-ups`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/proposals/analytics/summary`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      setOverview(overviewRes.data);
      setProposals(proposalsRes.data);
      setTasks(tasksRes.data);
      setLeads(Array.isArray(leadsRes.data) ? leadsRes.data : []);
      
      // Map analytics by proposal_id
      const aMap = {};
      (analyticsRes.data || []).forEach(a => { aMap[a.proposal_id] = a; });
      setAnalyticsMap(aMap);
    } catch (error) {
      console.error('Failed to fetch pipeline data:', error);
      toast.error('Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchEmailQueue = useCallback(async () => {
    setLoadingQueue(true);
    try {
      const res = await axios.get(`${API}/admin/email-queue`, { headers: { Authorization: `Bearer ${token}` } });
      setEmailQueue(Array.isArray(res.data) ? res.data : []);
    } catch { setEmailQueue([]); }
    finally { setLoadingQueue(false); }
  }, [token]);

  useEffect(() => {
    fetchData();
    fetchEmailQueue();
  }, [fetchData, fetchEmailQueue]);

  const sendProposal = async (proposalId) => {
    try {
      const response = await axios.post(`${API}/proposals/${proposalId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.email_sent === false) {
        toast.warning(response.data.message || 'Saved but email was NOT delivered — please re-send or check email config.', { duration: 8000 });
      } else {
        toast.success(response.data?.message || 'Sent');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send proposal');
    }
  };

  const openProposalAnalytics = async (proposal) => {
    setAnalyticsProposal(proposal);
    setShowAnalytics(true);
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/proposals/${proposal.proposal_id}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalyticsData(res.data);
    } catch {
      toast.error('Failed to load analytics');
      setAnalyticsData(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const copyProposalLink = async (proposal) => {
    try {
      const slug = proposal.slug || proposal.proposal_id;
      const url = `${window.location.origin}/proposal/${slug}`;
      await navigator.clipboard.writeText(url);
      toast.success('Proposal link copied to clipboard!');
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = `${window.location.origin}/proposal/${proposal.slug || proposal.proposal_id}`;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Proposal link copied to clipboard!');
    }
  };

  const saveTask = async () => {
    if (!taskForm.title) {
      toast.error('Task title is required');
      return;
    }

    try {
      const data = {
        ...taskForm,
        due_date: taskForm.due_date ? format(taskForm.due_date, 'yyyy-MM-dd') : null
      };

      if (editingTask) {
        await axios.patch(`${API}/follow-ups/${editingTask.task_id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Task updated!');
      } else {
        await axios.post(`${API}/follow-ups`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Task created!');
      }

      setShowTaskDialog(false);
      setEditingTask(null);
      setTaskForm({ title: '', description: '', due_date: null, priority: 'medium', related_to: '', related_type: 'proposal' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save task');
    }
  };

  const completeTask = async (taskId) => {
    try {
      await axios.patch(`${API}/follow-ups/${taskId}`, { status: 'completed' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task completed!');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API}/follow-ups/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const filteredProposals = proposals.filter(p => {
    if (selectedStatus !== 'all' && p.status !== selectedStatus) return false;
    if (docTypeFilter !== 'all' && (p.document_type || 'proposal') !== docTypeFilter) return false;
    return true;
  });

  // Leads filtering
  const filteredLeads = leads.filter(l => {
    if (leadFilter !== 'all' && l.status !== leadFilter) return false;
    if (leadSearch) {
      const q = leadSearch.toLowerCase();
      return (l.client_name || l.name || '').toLowerCase().includes(q) ||
        (l.client_email || l.email || '').toLowerCase().includes(q) ||
        (l.event_type || '').toLowerCase().includes(q);
    }
    return true;
  });
  const pagedLeads = filteredLeads.slice(leadPage * LEADS_PER_PAGE, (leadPage + 1) * LEADS_PER_PAGE);
  const totalLeadPages = Math.ceil(filteredLeads.length / LEADS_PER_PAGE);

  const leadStatusCounts = leads.reduce((acc, l) => {
    acc[l.status] = (acc[l.status] || 0) + 1;
    return acc;
  }, {});

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(prev => prev.map(l => l.lead_id === leadId ? { ...l, status: newStatus } : l));
      toast.success(`Lead updated to ${LEAD_STATUS_CONFIG[newStatus]?.label || newStatus}`);
    } catch (err) {
      toast.error('Failed to update lead');
    }
  };

  // Lead detail modal state
  const [showLeadDetail, setShowLeadDetail] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetail, setLeadDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailTab, setDetailTab] = useState('info');

  const SOURCE_ICONS = {
    ai_prospecting: <Bot className="h-3.5 w-3.5" />,
    sales_agent: <UserPlus className="h-3.5 w-3.5" />,
    planning_hub: <Globe className="h-3.5 w-3.5" />,
    direct: <Mail className="h-3.5 w-3.5" />,
  };

  const deleteLead = async (leadId) => {
    setDeleteTarget({ type: 'single', leadId });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'single') {
        await axios.delete(`${API}/leads/${deleteTarget.leadId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLeads(prev => prev.filter(l => l.lead_id !== deleteTarget.leadId));
        toast.success('Lead deleted');
      } else if (deleteTarget.type === 'bulk') {
        setBulkDeleting(true);
        const ids = [...selectedLeadIds];
        await Promise.all(ids.map(id =>
          axios.delete(`${API}/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } })
        ));
        setLeads(prev => prev.filter(l => !selectedLeadIds.has(l.lead_id)));
        toast.success(`${ids.length} lead${ids.length > 1 ? 's' : ''} deleted`);
        setSelectedLeadIds(new Set());
        setBulkDeleting(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
      setBulkDeleting(false);
    }
    setDeleteTarget(null);
  };

  const toggleLeadSelect = (leadId) => {
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedLeadIds.size === pagedLeads.length) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(pagedLeads.map(l => l.lead_id)));
    }
  };

  // Email queue actions
  const approveEmail = async (seqId, stepId) => {
    try {
      await axios.post(`${API}/admin/email-queue/${seqId}/steps/${stepId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email approved and sent');
      fetchEmailQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve email');
    }
  };

  const rejectEmail = async (seqId, stepId) => {
    try {
      await axios.post(`${API}/admin/email-queue/${seqId}/steps/${stepId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email rejected');
      fetchEmailQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject email');
    }
  };

  const saveEmailEdit = async () => {
    if (!editingEmail) return;
    try {
      await axios.put(`${API}/admin/email-queue/${editingEmail.sequence_id}/steps/${editingEmail.step_id}`, emailEditForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email content updated');
      setEditingEmail(null);
      fetchEmailQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update email');
    }
  };

  const openLeadDetail = async (lead) => {
    setSelectedLead(lead);
    setShowLeadDetail(true);
    setDetailTab('info');
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/leads/${lead.lead_id}/detail`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeadDetail(res.data);
    } catch (err) {
      toast.error('Failed to load lead details');
      setLeadDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Target className="h-8 w-8 text-indigo-500" />
              Sales Pipeline
            </h1>
            <p className="text-muted-foreground mt-1">Track proposals, leads, and follow-ups</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline"
              onClick={() => {
                setEditingTask(null);
                setTaskForm({ title: '', description: '', due_date: null, priority: 'medium', related_to: '', related_type: 'proposal' });
                setShowTaskDialog(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Task
            </Button>
            <Button 
              onClick={() => navigate('/admin/proposals/new')}
              className="bg-gradient-to-r from-indigo-500 to-purple-600"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Proposal
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="leads" data-testid="leads-tab">Leads ({leads.length})</TabsTrigger>
            <TabsTrigger value="email-queue" data-testid="email-queue-tab">
              Email Queue {emailQueue.length > 0 && <Badge className="ml-1.5 bg-amber-500 text-white text-[10px] px-1.5 py-0">{emailQueue.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="proposals">Proposals & Invoices</TabsTrigger>
            <TabsTrigger value="tasks">Follow-ups</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Leads</p>
                      <p className="text-3xl font-bold">{overview?.total_leads || 0}</p>
                    </div>
                    <Users className="h-10 w-10 text-blue-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Active Proposals</p>
                      <p className="text-3xl font-bold">
                        {(overview?.proposal_stats?.sent || 0) + (overview?.proposal_stats?.viewed || 0)}
                      </p>
                    </div>
                    <FileText className="h-10 w-10 text-amber-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Won Value</p>
                      <p className="text-3xl font-bold text-emerald-500">
                        ${(overview?.won_value || 0).toLocaleString()}
                      </p>
                    </div>
                    <DollarSign className="h-10 w-10 text-emerald-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Conversion Rate</p>
                      <p className="text-3xl font-bold text-indigo-500">
                        {(overview?.conversion_rate || 0).toFixed(1)}%
                      </p>
                    </div>
                    <TrendingUp className="h-10 w-10 text-indigo-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className={overview?.stale_quotes_count > 0 ? 'border-amber-500/50 bg-amber-50/5' : ''} data-testid="stale-quotes-card">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Needs Follow-up</p>
                      <p className={`text-3xl font-bold ${overview?.stale_quotes_count > 0 ? 'text-amber-500' : ''}`}>
                        {overview?.stale_quotes_count || 0}
                      </p>
                    </div>
                    <AlertTriangle className={`h-10 w-10 ${overview?.stale_quotes_count > 0 ? 'text-amber-500' : 'text-muted-foreground opacity-50'}`} />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Pipeline Funnel */}
            <div className="grid lg:grid-cols-2 gap-6 mb-8">
              <Card>
                <CardHeader>
                  <CardTitle>Proposal Pipeline</CardTitle>
                  <CardDescription>Track proposals through each stage</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(PROPOSAL_STATUS_CONFIG).map(([status, config]) => {
                      const count = overview?.proposal_stats?.[status] || 0;
                      const maxCount = Math.max(...Object.values(overview?.proposal_stats || { a: 1 }));
                      const width = count > 0 ? Math.max((count / maxCount) * 100, 15) : 5;
                      
                      return (
                        <div key={status} className="flex items-center gap-3">
                          <div className="w-24 flex items-center gap-2 text-sm">
                            {config.icon}
                            <span>{config.label}</span>
                          </div>
                          <div className="flex-1 h-8 bg-muted rounded-lg overflow-hidden">
                            <div 
                              className={`h-full ${config.color} flex items-center justify-end pr-2 transition-all`}
                              style={{ width: `${width}%` }}
                            >
                              <span className="text-sm font-bold">{count}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Pending Follow-ups</CardTitle>
                  <CardDescription>
                    {overview?.overdue_tasks_count > 0 && (
                      <span className="text-red-500">
                        <AlertCircle className="h-4 w-4 inline mr-1" />
                        {overview.overdue_tasks_count} overdue
                      </span>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {overview?.pending_tasks?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Bell className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No pending tasks</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {overview?.pending_tasks?.slice(0, 5).map(task => (
                        <div 
                          key={task.task_id}
                          className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                        >
                          <div className={`w-2 h-2 rounded-full ${
                            task.priority === 'high' ? 'bg-red-500' : 
                            task.priority === 'medium' ? 'bg-amber-500' : 'bg-green-500'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{task.title}</p>
                            {task.due_date && (
                              <p className="text-xs text-muted-foreground">
                                Due: {task.due_date}
                              </p>
                            )}
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => completeTask(task.task_id)}>
                            <CheckCircle2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Proposals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {overview?.recent_proposals?.map(proposal => {
                      const analytics = analyticsMap[proposal.proposal_id];
                      const levelColors = { hot: 'border-l-red-500', warm: 'border-l-amber-500', cool: 'border-l-blue-500', cold: 'border-l-slate-300' };
                      const borderClass = analytics ? levelColors[analytics.engagement_level] || '' : '';
                      
                      return (
                        <div 
                          key={proposal.proposal_id}
                          className={`flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 cursor-pointer border-l-4 ${borderClass}`}
                          onClick={() => navigate(`/admin/proposals/${proposal.proposal_id}`)}
                        >
                          <div className="min-w-0">
                            <p className="font-medium truncate">{proposal.client_name}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span>${proposal.total?.toFixed(2)}</span>
                              {analytics?.view_count > 0 && (
                                <span className="flex items-center gap-1">
                                  <Eye className="h-3 w-3" />{analytics.view_count}
                                </span>
                              )}
                            </div>
                          </div>
                          <Badge className={PROPOSAL_STATUS_CONFIG[proposal.status]?.color}>
                            {PROPOSAL_STATUS_CONFIG[proposal.status]?.label}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Pipeline Value</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-emerald-50 rounded-lg">
                      <div>
                        <p className="text-sm text-emerald-600">Won Deals</p>
                        <p className="text-2xl font-bold text-emerald-700">
                          ${(overview?.won_value || 0).toLocaleString()}
                        </p>
                      </div>
                      <CheckCircle2 className="h-10 w-10 text-emerald-500" />
                    </div>
                    <div className="flex justify-between items-center p-4 bg-amber-50 rounded-lg">
                      <div>
                        <p className="text-sm text-amber-600">Pending Proposals</p>
                        <p className="text-2xl font-bold text-amber-700">
                          ${(overview?.pending_value || 0).toLocaleString()}
                        </p>
                      </div>
                      <Clock className="h-10 w-10 text-amber-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads">
            {/* Status Filter */}
            <div className="flex gap-2 flex-wrap mb-4">
              <Button variant={leadFilter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => { setLeadFilter('all'); setLeadPage(0); }} data-testid="lead-filter-all">
                All ({leads.length})
              </Button>
              {Object.entries(LEAD_STATUS_CONFIG).map(([status, config]) => (
                <Button key={status} variant={leadFilter === status ? 'default' : 'outline'} size="sm"
                  onClick={() => { setLeadFilter(status); setLeadPage(0); }}
                  data-testid={`lead-filter-${status}`}
                >
                  <span className={`w-2 h-2 rounded-full ${config.color} mr-1.5`} />
                  {config.label} ({leadStatusCounts[status] || 0})
                </Button>
              ))}
            </div>

            {/* Search */}
            <div className="relative mb-4 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={leadSearch} onChange={e => { setLeadSearch(e.target.value); setLeadPage(0); }}
                placeholder="Search by name, email, or event type..." className="pl-9"
                data-testid="lead-search-input"
              />
            </div>

            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-muted-foreground">{filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''} found</p>
              <div className="flex items-center gap-2">
                {selectedLeadIds.size > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setDeleteTarget({ type: 'bulk' })}
                    disabled={bulkDeleting}
                    data-testid="bulk-delete-btn"
                  >
                    {bulkDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
                    Delete {selectedLeadIds.size} Selected
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={toggleSelectAll} data-testid="select-all-btn">
                  {selectedLeadIds.size === pagedLeads.length && pagedLeads.length > 0 ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
            </div>

            {/* Leads List */}
            <div className="space-y-2" data-testid="leads-list">
              {pagedLeads.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No leads found</h3>
                    <p className="text-muted-foreground">Try adjusting your filters or search</p>
                  </CardContent>
                </Card>
              ) : pagedLeads.map(lead => (
                <Card key={lead.lead_id} className={`hover:shadow-md transition-shadow ${selectedLeadIds.has(lead.lead_id) ? 'ring-2 ring-indigo-500' : ''}`} data-testid={`lead-card-${lead.lead_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedLeadIds.has(lead.lead_id)}
                        onChange={() => toggleLeadSelect(lead.lead_id)}
                        className="h-4 w-4 rounded border-border accent-indigo-500 shrink-0 cursor-pointer"
                        data-testid={`lead-checkbox-${lead.lead_id}`}
                        onClick={e => e.stopPropagation()}
                      />

                      {/* Status dot */}
                      <div className={`w-3 h-3 rounded-full shrink-0 ${LEAD_STATUS_CONFIG[lead.status]?.color || 'bg-gray-400'}`} />

                      {/* Lead info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="font-semibold text-sm truncate">{lead.client_name || lead.name || 'Unknown'}</p>
                          <Badge className={`${LEAD_STATUS_CONFIG[lead.status]?.color || 'bg-gray-500'} text-foreground text-[10px] px-1.5 py-0`}>
                            {LEAD_STATUS_CONFIG[lead.status]?.label || lead.status}
                          </Badge>
                          {lead.source && (
                            <span className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                              {SOURCE_ICONS[lead.source] || <Globe className="h-3 w-3" />}
                              {lead.source === 'ai_prospecting' ? 'AI' : lead.source?.replace(/_/g, ' ')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          {(lead.client_email || lead.email) && (
                            <span className="flex items-center gap-1 truncate max-w-[200px]">
                              <Mail className="h-3 w-3 shrink-0" />{lead.client_email || lead.email}
                            </span>
                          )}
                          {(lead.client_phone || lead.phone) && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />{lead.client_phone || lead.phone}
                            </span>
                          )}
                          {lead.event_type && (
                            <span className="capitalize">{lead.event_type}</span>
                          )}
                          {lead.event_date && (
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="h-3 w-3" />{lead.event_date?.slice(0, 10)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Budget */}
                      <div className="text-right shrink-0">
                        {lead.budget > 0 && (
                          <p className="text-sm font-bold text-emerald-500">${lead.budget.toLocaleString()}</p>
                        )}
                        {lead.guest_count > 0 && (
                          <p className="text-[10px] text-muted-foreground">{lead.guest_count} guests</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openLeadDetail(lead)} data-testid={`lead-view-${lead.lead_id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`lead-actions-${lead.lead_id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openLeadDetail(lead)} data-testid={`lead-detail-btn-${lead.lead_id}`}>
                              <Building2 className="h-4 w-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            {lead.status === 'new' && (
                              <DropdownMenuItem onClick={() => updateLeadStatus(lead.lead_id, 'contacted')}>
                                <Phone className="h-4 w-4 mr-2" /> Mark Contacted
                              </DropdownMenuItem>
                            )}
                            {['new', 'contacted'].includes(lead.status) && (
                              <DropdownMenuItem onClick={() => updateLeadStatus(lead.lead_id, 'proposal_sent')}>
                                <Send className="h-4 w-4 mr-2" /> Proposal Sent
                              </DropdownMenuItem>
                            )}
                            {lead.status !== 'won' && (
                              <DropdownMenuItem onClick={() => updateLeadStatus(lead.lead_id, 'won')}>
                                <CheckCircle2 className="h-4 w-4 mr-2 text-emerald-500" /> Mark Won
                              </DropdownMenuItem>
                            )}
                            {lead.status !== 'lost' && (
                              <DropdownMenuItem onClick={() => updateLeadStatus(lead.lead_id, 'lost')}>
                                <XCircle className="h-4 w-4 mr-2 text-red-500" /> Mark Lost
                              </DropdownMenuItem>
                            )}
                            {(lead.client_email || lead.email) && (
                              <DropdownMenuItem onClick={() => {
                                navigator.clipboard.writeText(lead.client_email || lead.email);
                                toast.success('Email copied');
                              }}>
                                <Copy className="h-4 w-4 mr-2" /> Copy Email
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => deleteLead(lead.lead_id)} className="text-red-500" data-testid={`lead-delete-btn-${lead.lead_id}`}>
                              <Trash2 className="h-4 w-4 mr-2" /> Delete Lead
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalLeadPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6" data-testid="leads-pagination">
                <Button variant="outline" size="sm" disabled={leadPage === 0}
                  onClick={() => setLeadPage(p => p - 1)}>
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {leadPage + 1} of {totalLeadPages}
                </span>
                <Button variant="outline" size="sm" disabled={leadPage >= totalLeadPages - 1}
                  onClick={() => setLeadPage(p => p + 1)}>
                  Next
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Email Queue Tab */}
          <TabsContent value="email-queue">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">Email Queue</h2>
                <p className="text-sm text-muted-foreground">Review, edit, approve or reject outgoing emails before they're sent</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchEmailQueue} data-testid="refresh-queue-btn">
                {loadingQueue ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
              </Button>
            </div>

            {loadingQueue ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
              </div>
            ) : emailQueue.length === 0 ? (
              <Card className="text-center py-16">
                <CardContent>
                  <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No pending emails</h3>
                  <p className="text-muted-foreground">All scheduled emails have been sent or approved</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="email-queue-list">
                {emailQueue.map((email, idx) => (
                  <Card key={`${email.sequence_id}-${email.step_id}`} className="hover:shadow-md transition-shadow" data-testid={`queue-item-${idx}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col lg:flex-row gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Mail className="h-4 w-4 text-indigo-500 shrink-0" />
                            <p className="font-semibold text-sm truncate">{email.subject || '(No subject)'}</p>
                            <Badge variant="outline" className="text-[10px]">Step {email.step_number}</Badge>
                            <Badge variant="outline" className={`text-[10px] ${email.mode === 'auto' ? 'text-amber-600 border-amber-200' : 'text-blue-600 border-blue-200'}`}>
                              {email.mode === 'auto' ? 'Auto' : 'Approval'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                            <span className="font-medium text-foreground">{email.lead_name}</span>
                            <span>{email.lead_email}</span>
                            {email.scheduled_at && (
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="h-3 w-3" />
                                {new Date(email.scheduled_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <div className="bg-muted/40 rounded-lg p-3 text-sm whitespace-pre-wrap max-h-32 overflow-y-auto">
                            {email.body}
                          </div>
                          {email.purpose && (
                            <p className="text-xs text-muted-foreground mt-1.5 italic">Purpose: {email.purpose}</p>
                          )}
                        </div>
                        <div className="flex lg:flex-col items-center gap-2 shrink-0">
                          <Button
                            size="sm"
                            onClick={() => approveEmail(email.sequence_id, email.step_id)}
                            className="bg-emerald-500 hover:bg-emerald-600"
                            data-testid={`approve-email-${idx}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Approve & Send
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingEmail(email);
                              setEmailEditForm({ subject: email.subject, body: email.body });
                            }}
                            data-testid={`edit-email-${idx}`}
                          >
                            <Edit className="h-4 w-4 mr-1" /> Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => rejectEmail(email.sequence_id, email.step_id)}
                            data-testid={`reject-email-${idx}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Proposals Tab */}
          <TabsContent value="proposals">
            {/* Doc Type Filter */}
            <div className="flex gap-2 flex-wrap mb-3">
              <Button variant={docTypeFilter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setDocTypeFilter('all')}>All ({proposals.length})</Button>
              <Button variant={docTypeFilter === 'proposal' ? 'default' : 'outline'} size="sm" onClick={() => setDocTypeFilter('proposal')}>Proposals ({proposals.filter(p => (p.document_type || 'proposal') === 'proposal').length})</Button>
              <Button variant={docTypeFilter === 'invoice' ? 'default' : 'outline'} size="sm" onClick={() => setDocTypeFilter('invoice')} className="gap-1">Invoices ({proposals.filter(p => p.document_type === 'invoice').length})</Button>
            </div>
            {/* Status Filter */}
            <div className="flex gap-2 flex-wrap mb-6">
              <Button 
                variant={selectedStatus === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedStatus('all')}
              >
                All ({proposals.length})
              </Button>
              {Object.entries(PROPOSAL_STATUS_CONFIG).map(([status, config]) => {
                const count = proposals.filter(p => p.status === status).length;
                return (
                  <Button
                    key={status}
                    variant={selectedStatus === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedStatus(status)}
                    className="gap-1"
                  >
                    {config.icon} {config.label} ({count})
                  </Button>
                );
              })}
            </div>

            {/* Proposals List */}
            <div className="space-y-4">
              {filteredProposals.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No proposals or invoices found</h3>
                    <p className="text-muted-foreground mb-4">Create your first proposal or invoice to get started</p>
                    <div className="flex gap-2 justify-center">
                      <Button onClick={() => navigate('/admin/proposals/new')}>
                        <Plus className="h-4 w-4 mr-2" /> Create Proposal
                      </Button>
                      <Button variant="outline" onClick={() => navigate('/admin/proposals/new?type=invoice')}>
                        <Plus className="h-4 w-4 mr-2" /> Create Invoice
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                filteredProposals.map(proposal => (
                  <Card key={proposal.proposal_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row gap-4">
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-semibold text-lg">{proposal.client_name}</h3>
                              {proposal.company_name && (
                                <p className="text-muted-foreground text-sm">{proposal.company_name}</p>
                              )}
                            </div>
                            <Badge className={PROPOSAL_STATUS_CONFIG[proposal.status]?.color}>
                              {PROPOSAL_STATUS_CONFIG[proposal.status]?.icon}
                              <span className="ml-1">{PROPOSAL_STATUS_CONFIG[proposal.status]?.label}</span>
                            </Badge>
                            {proposal.document_type === 'invoice' && (
                              <Badge className="bg-emerald-600 text-white text-[10px] ml-1">INV</Badge>
                            )}
                            {proposal.document_type === 'invoice' && proposal.payment_status && (
                              <Badge className={
                                proposal.payment_status === 'paid' ? 'bg-emerald-500 text-white' :
                                proposal.payment_status === 'partial_payment' ? 'bg-amber-500 text-white' :
                                proposal.payment_status === 'overdue' ? 'bg-red-500 text-white' :
                                'bg-slate-400 text-white'
                              }>
                                {proposal.payment_status === 'partial_payment' ? `Partial ($${(proposal.amount_paid || 0).toFixed(0)})` : proposal.payment_status.charAt(0).toUpperCase() + proposal.payment_status.slice(1)}
                              </Badge>
                            )}
                          </div>
                          
                          <p className="text-sm text-muted-foreground mb-3">
                            {proposal.event_name || proposal.title}
                            {proposal.event_date && ` • ${proposal.event_date.slice(0, 10)}`}
                          </p>

                          <div className="flex items-center gap-4 text-sm">
                            <span className="font-bold text-lg text-indigo-600">
                              ${proposal.total?.toFixed(2)}
                            </span>
                            {proposal.document_type === 'invoice' && proposal.amount_paid > 0 && proposal.payment_status !== 'paid' && (
                              <span className="text-xs text-emerald-600 font-medium">Paid: ${(proposal.amount_paid || 0).toFixed(2)}</span>
                            )}
                            {proposal.document_type === 'invoice' && proposal.invoice_number && (
                              <span className="text-xs text-muted-foreground">{proposal.invoice_number}</span>
                            )}
                            {/* Engagement Signal */}
                            {(() => {
                              const analytics = analyticsMap[proposal.proposal_id];
                              if (!analytics) return null;
                              const levelConfig = {
                                hot: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Hot' },
                                warm: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Warm' },
                                cool: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Cool' },
                                cold: { color: 'bg-slate-100 text-muted-foreground border-slate-200', label: 'Cold' }
                              };
                              const cfg = levelConfig[analytics.engagement_level] || levelConfig.cold;
                              return (
                                <Badge data-testid="engagement-badge" className={`${cfg.color} border text-xs`}>
                                  {cfg.label} ({analytics.engagement_score}%)
                                </Badge>
                              );
                            })()}
                            {proposal.view_count > 0 && (
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <Eye className="h-4 w-4" /> {proposal.view_count} views
                              </span>
                            )}
                            {analyticsMap[proposal.proposal_id]?.total_time_seconds > 0 && (
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <Clock className="h-4 w-4" /> 
                                {Math.round(analyticsMap[proposal.proposal_id].total_time_seconds / 60)}m spent
                              </span>
                            )}
                            {analyticsMap[proposal.proposal_id]?.last_viewed_at && (
                              <span className="text-muted-foreground text-xs">
                                Last viewed: {new Date(analyticsMap[proposal.proposal_id].last_viewed_at).toLocaleDateString()}
                              </span>
                            )}
                            {proposal.sent_at && (
                              <span className="text-muted-foreground">
                                Sent: {new Date(proposal.sent_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>

                          {/* Pending Tasks */}
                          {proposal.needs_followup && (
                            <div className="mt-3 flex items-center gap-2" data-testid={`needs-followup-${proposal.proposal_id}`}>
                              <AlertTriangle className="h-4 w-4 text-amber-500" />
                              <span className="text-sm text-amber-500 font-medium">
                                Needs Follow-up — sent 3+ days ago, not viewed
                              </span>
                            </div>
                          )}
                          {proposal.is_quick_quote && (
                            <Badge className="mt-2 bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs" data-testid={`quick-quote-badge-${proposal.proposal_id}`}>
                              Quick Quote
                            </Badge>
                          )}
                          {proposal.pending_tasks?.length > 0 && (
                            <div className="mt-3 flex items-center gap-2">
                              <Bell className="h-4 w-4 text-amber-500" />
                              <span className="text-sm text-amber-600">
                                {proposal.pending_tasks.length} pending follow-up(s)
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {proposal.status === 'draft' && (
                            <Button 
                              size="sm"
                              onClick={() => sendProposal(proposal.proposal_id)}
                              className="bg-blue-500 hover:bg-blue-600"
                            >
                              <Send className="h-4 w-4 mr-1" /> Send
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyProposalLink(proposal)}
                          >
                            <Copy className="h-4 w-4 mr-1" /> Copy Link
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`/proposal/${proposal.slug || proposal.proposal_id}`, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                setTaskForm({
                                  title: `Follow up on ${proposal.client_name}'s proposal`,
                                  description: '',
                                  due_date: null,
                                  priority: 'medium',
                                  related_to: proposal.proposal_id,
                                  related_type: 'proposal'
                                });
                                setShowTaskDialog(true);
                              }}>
                                <Bell className="h-4 w-4 mr-2" />
                                Add Follow-up
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/admin/proposals/${proposal.proposal_id}`)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openProposalAnalytics(proposal)} data-testid={`analytics-btn-${proposal.proposal_id}`}>
                                <BarChart3 className="h-4 w-4 mr-2" />
                                Analytics
                              </DropdownMenuItem>
                              {proposal.document_type === 'invoice' && proposal.payment_status !== 'paid' && (
                                <>
                                  <DropdownMenuItem onClick={async () => {
                                    const amount = prompt(`Record payment for ${proposal.client_name}\nTotal: $${proposal.total?.toFixed(2)}\nPaid so far: $${(proposal.amount_paid || 0).toFixed(2)}\n\nEnter payment amount:`);
                                    if (amount && parseFloat(amount) > 0) {
                                      const method = prompt('Payment method? (cash, check, transfer, stripe, other)', 'cash');
                                      try {
                                        await axios.post(`${API}/proposals/${proposal.proposal_id}/record-payment`, { amount: parseFloat(amount), method: method || 'other' }, { headers: { Authorization: `Bearer ${token}` } });
                                        toast.success(`$${parseFloat(amount).toFixed(2)} payment recorded`);
                                        fetchData();
                                      } catch { toast.error('Failed to record payment'); }
                                    }
                                  }}>
                                    <DollarSign className="h-4 w-4 mr-2" />
                                    Record Payment
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={async () => {
                                    try {
                                      await axios.post(`${API}/proposals/${proposal.proposal_id}/mark-paid`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                      toast.success('Invoice marked as paid');
                                      fetchData();
                                    } catch { toast.error('Failed to update'); }
                                  }}>
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Mark as Paid
                                  </DropdownMenuItem>
                                </>
                              )}
                              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={async () => {
                                if (!confirm(`Delete this ${proposal.document_type === 'invoice' ? 'invoice' : 'proposal'} for ${proposal.client_name}? This cannot be undone.`)) return;
                                try {
                                  await axios.delete(`${API}/proposals/${proposal.proposal_id}`, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success('Deleted');
                                  fetchData();
                                } catch { toast.error('Failed to delete'); }
                              }}>
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Pending */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-amber-600">
                    <Clock className="h-5 w-5" />
                    Pending
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {tasks.filter(t => t.status === 'pending').map(task => (
                    <div 
                      key={task.task_id}
                      className="p-4 bg-muted/30 rounded-lg border border-border"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          task.priority === 'high' ? 'bg-red-500' : 
                          task.priority === 'medium' ? 'bg-amber-500' : 'bg-green-500'
                        }`} />
                        <div className="flex-1 mx-3">
                          <p className="font-medium">{task.title}</p>
                          {task.description && (
                            <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                          )}
                          {task.due_date && (
                            <p className={`text-xs mt-2 ${
                              new Date(task.due_date) < new Date() ? 'text-red-500' : 'text-muted-foreground'
                            }`}>
                              <CalendarIcon className="h-3 w-3 inline mr-1" />
                              {task.due_date}
                            </p>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => completeTask(task.task_id)}>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Complete
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setEditingTask(task);
                              setTaskForm({
                                title: task.title,
                                description: task.description || '',
                                due_date: task.due_date ? new Date(task.due_date) : null,
                                priority: task.priority,
                                related_to: task.related_to || '',
                                related_type: task.related_type || 'proposal'
                              });
                              setShowTaskDialog(true);
                            }}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => deleteTask(task.task_id)} className="text-red-500">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                  {tasks.filter(t => t.status === 'pending').length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No pending tasks</p>
                  )}
                </CardContent>
              </Card>

              {/* Completed */}
              <Card className="lg:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-emerald-600">
                    <CheckCircle2 className="h-5 w-5" />
                    Completed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {tasks.filter(t => t.status === 'completed').slice(0, 10).map(task => (
                      <div 
                        key={task.task_id}
                        className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg text-sm"
                      >
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        <span className="flex-1 line-through text-muted-foreground">{task.title}</span>
                        <span className="text-xs text-muted-foreground">
                          {task.completed_at?.slice(0, 10)}
                        </span>
                      </div>
                    ))}
                    {tasks.filter(t => t.status === 'completed').length === 0 && (
                      <p className="text-center text-muted-foreground py-8">No completed tasks</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Task Dialog */}
        <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingTask ? 'Edit Task' : 'Add Follow-up Task'}</DialogTitle>
              <DialogDescription>
                Create a reminder to follow up on a lead or proposal
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="e.g., Follow up on proposal"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {taskForm.due_date ? format(taskForm.due_date, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={taskForm.due_date}
                        onSelect={(date) => setTaskForm({ ...taskForm, due_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select 
                    value={taskForm.priority}
                    onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
              <Button onClick={saveTask}>
                {editingTask ? 'Update Task' : 'Create Task'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Lead Detail Modal */}
        <Dialog open={showLeadDetail} onOpenChange={(open) => { if (!open) { setShowLeadDetail(false); setSelectedLead(null); setLeadDetail(null); } }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lead-detail-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-indigo-500" />
                {selectedLead?.client_name || selectedLead?.name || 'Lead Details'}
              </DialogTitle>
              <DialogDescription>
                Full lead information, emails, and activity history
              </DialogDescription>
            </DialogHeader>

            {loadingDetail ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
              </div>
            ) : leadDetail ? (
              <div className="space-y-4">
                {/* Detail Tabs */}
                <div className="flex gap-1 border-b">
                  {[
                    { id: 'info', label: 'Contact & Details', icon: <Users className="h-3.5 w-3.5" /> },
                    { id: 'emails', label: `Emails (${leadDetail.sent_emails?.length || 0})`, icon: <Mail className="h-3.5 w-3.5" /> },
                    { id: 'activity', label: 'Activity', icon: <Clock className="h-3.5 w-3.5" /> },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setDetailTab(tab.id)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                        detailTab === tab.id 
                          ? 'border-indigo-500 text-indigo-600' 
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`detail-tab-${tab.id}`}
                    >
                      {tab.icon} {tab.label}
                    </button>
                  ))}
                </div>

                {/* Info Tab */}
                {detailTab === 'info' && (
                  <div className="space-y-4" data-testid="lead-info-tab">
                    {/* Status Badge */}
                    <div className="flex items-center gap-2">
                      <Badge className={`${LEAD_STATUS_CONFIG[leadDetail.lead?.status]?.color || 'bg-gray-500'} text-white`}>
                        {LEAD_STATUS_CONFIG[leadDetail.lead?.status]?.label || leadDetail.lead?.status}
                      </Badge>
                      {leadDetail.lead?.source && (
                        <Badge variant="outline" className="text-xs">
                          {SOURCE_ICONS[leadDetail.lead.source] || <Globe className="h-3 w-3" />}
                          <span className="ml-1">{leadDetail.lead.source === 'ai_prospecting' ? 'AI Prospecting' : leadDetail.lead.source?.replace(/_/g, ' ')}</span>
                        </Badge>
                      )}
                    </div>

                    {/* Contact Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      {leadDetail.lead?.client_email && (
                        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                          <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs text-muted-foreground">Email</p>
                            <p className="text-sm font-medium truncate">{leadDetail.lead.client_email}</p>
                          </div>
                        </div>
                      )}
                      {leadDetail.lead?.client_phone && (
                        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                          <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs text-muted-foreground">Phone</p>
                            <p className="text-sm font-medium">{leadDetail.lead.client_phone}</p>
                          </div>
                        </div>
                      )}
                      {leadDetail.lead?.event_type && (
                        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                          <CalendarIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs text-muted-foreground">Event Type</p>
                            <p className="text-sm font-medium capitalize">{leadDetail.lead.event_type}</p>
                          </div>
                        </div>
                      )}
                      {leadDetail.lead?.event_date && (
                        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                          <CalendarIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs text-muted-foreground">Event Date</p>
                            <p className="text-sm font-medium">{leadDetail.lead.event_date?.slice(0, 10)}</p>
                          </div>
                        </div>
                      )}
                      {leadDetail.lead?.budget > 0 && (
                        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                          <DollarSign className="h-4 w-4 text-emerald-500 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs text-muted-foreground">Budget</p>
                            <p className="text-sm font-bold text-emerald-500">${leadDetail.lead.budget.toLocaleString()}</p>
                          </div>
                        </div>
                      )}
                      {leadDetail.lead?.guest_count > 0 && (
                        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                          <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs text-muted-foreground">Guest Count</p>
                            <p className="text-sm font-medium">{leadDetail.lead.guest_count}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    {leadDetail.lead?.notes && (
                      <div className="p-3 bg-muted/30 rounded-lg border">
                        <p className="text-xs text-muted-foreground mb-1 font-medium">Notes</p>
                        <p className="text-sm whitespace-pre-wrap">{leadDetail.lead.notes}</p>
                      </div>
                    )}

                    {/* Sequences Summary */}
                    {leadDetail.sequences?.length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground font-medium mb-2">Email Sequences</p>
                        <div className="space-y-2">
                          {leadDetail.sequences.map(seq => (
                            <div key={seq.sequence_id} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg text-sm">
                              <div className="flex items-center gap-2">
                                <Mail className="h-3.5 w-3.5 text-indigo-500" />
                                <span>{seq.completed_steps || 0}/{seq.total_steps || 0} steps</span>
                              </div>
                              <Badge variant="outline" className="text-xs capitalize">{seq.status}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Timestamps */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t">
                      <span>Created: {leadDetail.lead?.created_at ? new Date(leadDetail.lead.created_at).toLocaleDateString() : 'N/A'}</span>
                      <span>Updated: {leadDetail.lead?.updated_at ? new Date(leadDetail.lead.updated_at).toLocaleDateString() : 'N/A'}</span>
                    </div>
                  </div>
                )}

                {/* Emails Tab */}
                {detailTab === 'emails' && (
                  <div className="space-y-3" data-testid="lead-emails-tab">
                    {leadDetail.sent_emails?.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Mail className="h-10 w-10 mx-auto mb-2 opacity-40" />
                        <p className="text-sm">No emails sent to this lead yet</p>
                      </div>
                    ) : (
                      leadDetail.sent_emails?.map((email, idx) => (
                        <div key={idx} className="border rounded-lg overflow-hidden" data-testid={`email-item-${idx}`}>
                          <div className="flex items-center justify-between p-3 bg-muted/30 cursor-pointer"
                            onClick={(e) => {
                              const body = e.currentTarget.nextElementSibling;
                              body.classList.toggle('hidden');
                            }}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Mail className="h-4 w-4 text-indigo-500 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{email.subject || '(No subject)'}</p>
                                <p className="text-xs text-muted-foreground">
                                  {email.sent_at ? new Date(email.sent_at).toLocaleString() : 'Unknown date'}
                                  {email.type === 'sequence' && email.step_number && ` • Step ${email.step_number}`}
                                </p>
                              </div>
                            </div>
                            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 transition-transform" />
                          </div>
                          <div className="hidden p-3 border-t bg-background">
                            <p className="text-sm whitespace-pre-wrap">{email.body}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Activity Tab */}
                {detailTab === 'activity' && (
                  <div className="space-y-3" data-testid="lead-activity-tab">
                    {leadDetail.outreach_history?.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Clock className="h-10 w-10 mx-auto mb-2 opacity-40" />
                        <p className="text-sm">No activity recorded yet</p>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
                        {leadDetail.outreach_history?.map((activity, idx) => (
                          <div key={idx} className="relative flex gap-3 pb-4 pl-8" data-testid={`activity-item-${idx}`}>
                            <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 border-background ${
                              activity.type === 'email' ? 'bg-indigo-500' :
                              activity.type === 'call' ? 'bg-emerald-500' :
                              activity.type === 'meeting' ? 'bg-amber-500' : 'bg-gray-400'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <Badge variant="outline" className="text-[10px] capitalize">{activity.type}</Badge>
                                {activity.outcome && (
                                  <Badge variant="outline" className={`text-[10px] ${
                                    activity.outcome === 'positive' ? 'text-emerald-600 border-emerald-200' :
                                    activity.outcome === 'negative' ? 'text-red-600 border-red-200' : ''
                                  }`}>{activity.outcome}</Badge>
                                )}
                              </div>
                              {activity.subject && <p className="text-sm font-medium">{activity.subject}</p>}
                              {activity.notes && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{activity.notes}</p>}
                              <p className="text-xs text-muted-foreground mt-1">
                                {activity.created_at ? new Date(activity.created_at).toLocaleString() : ''}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p>Failed to load lead details</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
          <DialogContent className="max-w-md" data-testid="delete-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-500">
                <Trash2 className="h-5 w-5" />
                {deleteTarget?.type === 'bulk' ? `Delete ${selectedLeadIds.size} Leads?` : 'Delete Lead?'}
              </DialogTitle>
              <DialogDescription>
                {deleteTarget?.type === 'bulk'
                  ? `This will permanently delete ${selectedLeadIds.size} selected lead${selectedLeadIds.size > 1 ? 's' : ''} and all associated data (outreach, sequences). This cannot be undone.`
                  : 'This will permanently delete this lead and all associated data (outreach, sequences). This cannot be undone.'
                }
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button variant="destructive" onClick={confirmDelete} disabled={bulkDeleting} data-testid="confirm-delete-btn">
                {bulkDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email Edit Dialog */}
        <Dialog open={!!editingEmail} onOpenChange={(open) => { if (!open) setEditingEmail(null); }}>
          <DialogContent className="max-w-xl" data-testid="email-edit-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-indigo-500" />
                Edit Email Content
              </DialogTitle>
              <DialogDescription>
                To: {editingEmail?.lead_name} ({editingEmail?.lead_email})
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input
                  value={emailEditForm.subject}
                  onChange={e => setEmailEditForm(prev => ({ ...prev, subject: e.target.value }))}
                  data-testid="email-edit-subject"
                />
              </div>
              <div className="space-y-2">
                <Label>Email Body</Label>
                <Textarea
                  value={emailEditForm.body}
                  onChange={e => setEmailEditForm(prev => ({ ...prev, body: e.target.value }))}
                  rows={10}
                  data-testid="email-edit-body"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingEmail(null)}>Cancel</Button>
              <Button onClick={saveEmailEdit} data-testid="email-edit-save">
                Save Changes
              </Button>
              <Button
                onClick={async () => {
                  await saveEmailEdit();
                  if (editingEmail) await approveEmail(editingEmail.sequence_id, editingEmail.step_id);
                }}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="email-edit-save-approve"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Save & Approve
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Proposal Analytics Dialog */}
        <Dialog open={showAnalytics} onOpenChange={setShowAnalytics}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="proposal-analytics-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-indigo-500" />
                Proposal Analytics
              </DialogTitle>
              {analyticsProposal && (
                <DialogDescription>
                  {analyticsProposal.client_name} — {analyticsProposal.event_name || analyticsProposal.title}
                </DialogDescription>
              )}
            </DialogHeader>

            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
              </div>
            ) : analyticsData ? (
              <div className="space-y-6 py-2">
                {/* Engagement Score */}
                <div className="flex items-center gap-6">
                  <div className="relative w-24 h-24">
                    <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="8" className="text-muted/20" />
                      <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8"
                        className={analyticsData.engagement_level === 'hot' ? 'text-red-500' : analyticsData.engagement_level === 'warm' ? 'text-amber-500' : analyticsData.engagement_level === 'cool' ? 'text-blue-500' : 'text-slate-400'}
                        stroke="currentColor"
                        strokeDasharray={`${analyticsData.engagement_score * 2.64} 264`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-xl font-bold">{analyticsData.engagement_score}</span>
                      <span className="text-[10px] text-muted-foreground">/ 100</span>
                    </div>
                  </div>
                  <div>
                    <Badge data-testid="analytics-engagement-level" className={`text-sm px-3 py-1 ${
                      analyticsData.engagement_level === 'hot' ? 'bg-red-100 text-red-700 border-red-200' :
                      analyticsData.engagement_level === 'warm' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                      analyticsData.engagement_level === 'cool' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                      'bg-slate-100 text-slate-600 border-slate-200'
                    } border`}>
                      {analyticsData.engagement_level?.charAt(0).toUpperCase() + analyticsData.engagement_level?.slice(1)} Lead
                    </Badge>
                    <p className="text-sm text-muted-foreground mt-2">
                      Based on views, time spent, scroll depth, section engagement & CTA clicks
                    </p>
                  </div>
                </div>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="analytics-metrics-grid">
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <Eye className="h-5 w-5 mx-auto mb-1 text-blue-500" />
                    <p className="text-2xl font-bold">{analyticsData.view_count}</p>
                    <p className="text-xs text-muted-foreground">Page Views</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <Clock className="h-5 w-5 mx-auto mb-1 text-emerald-500" />
                    <p className="text-2xl font-bold">
                      {analyticsData.total_time_seconds >= 60
                        ? `${Math.floor(analyticsData.total_time_seconds / 60)}m ${analyticsData.total_time_seconds % 60}s`
                        : `${analyticsData.total_time_seconds}s`}
                    </p>
                    <p className="text-xs text-muted-foreground">Time Spent</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <ChevronRight className="h-5 w-5 mx-auto mb-1 text-amber-500" />
                    <p className="text-2xl font-bold">{analyticsData.max_scroll_depth}%</p>
                    <p className="text-xs text-muted-foreground">Scroll Depth</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <Target className="h-5 w-5 mx-auto mb-1 text-red-500" />
                    <p className="text-2xl font-bold">{analyticsData.cta_clicks}</p>
                    <p className="text-xs text-muted-foreground">CTA Clicks</p>
                  </div>
                </div>

                {/* Last Viewed */}
                {analyticsData.last_viewed_at && (
                  <p className="text-sm text-muted-foreground">
                    Last viewed: <span className="font-medium text-foreground">{new Date(analyticsData.last_viewed_at).toLocaleString()}</span>
                  </p>
                )}

                {/* Section Engagement */}
                {Object.keys(analyticsData.section_engagement || {}).length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-3">Section Engagement</h4>
                    <div className="space-y-2" data-testid="analytics-section-engagement">
                      {Object.entries(analyticsData.section_engagement)
                        .sort(([,a], [,b]) => b - a)
                        .map(([section, count]) => {
                          const maxCount = Math.max(...Object.values(analyticsData.section_engagement));
                          return (
                            <div key={section} className="flex items-center gap-3">
                              <span className="text-xs text-muted-foreground w-28 truncate capitalize">{section.replace(/_/g, ' ')}</span>
                              <div className="flex-1 bg-muted/30 rounded-full h-2.5">
                                <div
                                  className="bg-indigo-500 h-2.5 rounded-full transition-all"
                                  style={{ width: `${(count / maxCount) * 100}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium w-8 text-right">{count}</span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Recent Activity */}
                {analyticsData.recent_events?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-3">Recent Activity</h4>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="analytics-recent-events">
                      {analyticsData.recent_events.slice(0, 15).map((evt, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-muted/20">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${
                              evt.event_type === 'page_view' ? 'bg-blue-500' :
                              evt.event_type === 'cta_click' ? 'bg-red-500' :
                              evt.event_type === 'section_view' ? 'bg-amber-500' :
                              evt.event_type === 'scroll' ? 'bg-emerald-500' : 'bg-slate-400'
                            }`} />
                            <span className="capitalize">{evt.event_type.replace(/_/g, ' ')}</span>
                            {evt.data?.section && <span className="text-muted-foreground">— {evt.data.section}</span>}
                            {evt.data?.depth && <span className="text-muted-foreground">— {evt.data.depth}%</span>}
                            {evt.data?.seconds && <span className="text-muted-foreground">— {evt.data.seconds}s</span>}
                          </div>
                          <span className="text-muted-foreground">{new Date(evt.timestamp).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No data state */}
                {analyticsData.view_count === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    <Eye className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p className="font-medium">No views yet</p>
                    <p className="text-sm">Analytics will appear after the client opens the proposal</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No analytics data available</p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
